import Importer from 'hello-csv/react';
import helloCsvStyles from '../../../../../node_modules/hello-csv/dist/react/hello-csv.css?raw';
import type { ImporterState, SheetRow, SheetState } from 'hello-csv/react';
import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import Modal from '../../../Modal';
import { importParticipantsV2Store } from '../Store';
import { ParticipantCsvRow } from '../Store/Types';
import { createHelloCsvSheets } from './config';
import { tealThemeOverrides } from './styles';

interface HelloCSVImporterProps {
  onClose: () => void;
}

function HelloCSVImporter({ onClose }: HelloCSVImporterProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Inject hello-csv styles (bypassing PostCSS which doesn't support @layer)
  useEffect(() => {
    const styleId = 'hello-csv-styles';
    const themeStyleId = 'hello-csv-theme-overrides';

    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = helloCsvStyles;
      document.head.appendChild(style);
    }

    // Add teal theme overrides
    if (!document.getElementById(themeStyleId)) {
      const themeStyle = document.createElement('style');
      themeStyle.id = themeStyleId;
      themeStyle.textContent = tealThemeOverrides;
      document.head.appendChild(themeStyle);
    }

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
      const existingThemeStyle = document.getElementById(themeStyleId);
      if (existingThemeStyle) {
        existingThemeStyle.remove();
      }
    };
  }, []);

  // The Modal focus trap auto-focuses the first tabbable element, which here
  // is hello-csv's first column info icon — focusing it shows its tooltip as
  // soon as the overlay opens. Redirect initial focus to the modal container
  // (tabIndex={-1}) after the trap activates; Tab still enters the dialog.
  useEffect(() => {
    const id = window.setTimeout(() => {
      document.getElementById('modal-container')?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, []);

  // Add bottom padding to the HelloCSV table container so error tooltips
  // on the last rows have room to display without scrolling the content away
  useEffect(() => {
    let cleanupFn: (() => void) | null = null;

    const addBottomPadding = () => {
      const container = document.querySelector(
        '.hello-csv .min-h-0.flex-1.overflow-auto',
      ) as HTMLElement | null;
      if (!container) return;

      // Add padding to the last element inside the scroll container
      // so error tooltips on bottom rows have space to render
      const table = container.querySelector('table');
      if (table) {
        table.style.marginBottom = '40px';
      }

      cleanupFn = () => {
        if (table) {
          table.style.marginBottom = '';
        }
      };
    };

    const observer = new MutationObserver(() => {
      const container = document.querySelector('.hello-csv .min-h-0.flex-1.overflow-auto');
      if (container) {
        observer.disconnect();
        addBottomPadding();
      }
    });

    const outerContainer = scrollContainerRef.current;
    if (outerContainer) {
      observer.observe(outerContainer, { childList: true, subtree: true });
      addBottomPadding();
    }

    return () => {
      observer.disconnect();
      if (cleanupFn) cleanupFn();
    };
  }, []);

  const handleComplete = useCallback(
    async (
      state: ImporterState,
      _onProgress: (progress: number) => void,
      _submissionFiles: unknown[],
    ): Promise<void> => {
      // Save the HelloCSV state for later restoration
      importParticipantsV2Store.saveHelloCsvState(state);

      // Get the participants sheet data
      const participantsSheet = state.sheetData.find(
        (s: SheetState) => s.sheetId === 'participants',
      );
      if (!participantsSheet) {
        return;
      }

      // Convert sheet rows to our participant format
      const participants: ParticipantCsvRow[] = participantsSheet.rows
        .filter((row: SheetRow) => row.email && row.firstname && row.lastname)
        .map((row: SheetRow) => ({
          email: String(row.email || '').trim(),
          firstname: String(row.firstname || '').trim(),
          lastname: String(row.lastname || '').trim(),
          login_type: fromNullable(row.login_type ? String(row.login_type) : null),
        }));

      // Get filename from rowFile if available
      const filename = state.rowFile?.name || 'import.csv';

      // Update store with processed data
      importParticipantsV2Store.fileProcessed(participants, filename);
    },
    [],
  );

  // Get saved state and set mode to preview if it exists
  const initialState = useMemo(
    () =>
      importParticipantsV2Store.savedHelloCsvState
        ? { ...importParticipantsV2Store.savedHelloCsvState, mode: 'preview' as const }
        : undefined,
    [],
  );

  const handleSummaryFinished = useCallback(() => {
    onClose();
  }, [onClose]);

  // Create sheets with domain validation based on allowed domains from the store
  const helloCsvSheets = useMemo(
    () => createHelloCsvSheets(importParticipantsV2Store.allowedEmailDomains),
    [],
  );

  return (
    <Modal
      close={onClose}
      title={<></>}
      modalWidth="w-full max-w-4xl"
      bodyPadding="p-0"
      contentPadding="p-0"
      ariaLabel="Import CSV"
      customBackgroundCssClasses="bg-black/50"
      closeOnBackdropClick={false}
      additionalClasses="h-[85vh] overflow-hidden"
    >
      {/* Bound the importer to a concrete height (the Modal's content wrapper
          doesn't propagate height, and it force-adds overflow-y-auto). This
          keeps hello-csv scrolling its table internally so its footer action
          button stays visible without scrolling the whole modal. */}
      <div ref={scrollContainerRef} className="h-[calc(85vh-3rem)] overflow-hidden py-6 px-8">
        <Importer
          sheets={helloCsvSheets}
          onComplete={handleComplete}
          onSummaryFinished={handleSummaryFinished}
          preventUploadOnValidationErrors={true}
          availableActions={[
            'addRows',
            'removeRows',
            'editRows',
            'search',
            'downloadCsv',
            'backToPreviousStep',
          ]}
          initialState={initialState}
        />
      </div>
    </Modal>
  );
}

export default observer(HelloCSVImporter);
